import React from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface OverridePermissionOption {
  id: number;
  label: string;
  isCertainPathOnly: boolean;
}

export interface OverrideRow {
  id: number;
  permissionLabel: string;
  permissionInternalName: string;
  overrideType: "grant" | "revoke";
}

interface PermissionOverridesEditorProps {
  description: string;
  availablePermissions: OverridePermissionOption[];
  overrides: OverrideRow[];
  selectedPermissionId: string;
  selectedOverrideType: "grant" | "revoke";
  onSelectedPermissionIdChange: (permissionId: string) => void;
  onSelectedOverrideTypeChange: (overrideType: "grant" | "revoke") => void;
  onAddOverride: () => void;
  onRemoveOverride: (overrideId: number) => void;
  permissionsLoading: boolean;
  overrideLoading: boolean;
}

const PermissionOverridesEditor: React.FC<PermissionOverridesEditorProps> = ({
  description,
  availablePermissions,
  overrides,
  selectedPermissionId,
  selectedOverrideType,
  onSelectedPermissionIdChange,
  onSelectedOverrideTypeChange,
  onAddOverride,
  onRemoveOverride,
  permissionsLoading,
  overrideLoading,
}) => (
  <div className="space-y-4">
    <div>
      <h3 className="font-medium text-sm text-gray-900 dark:text-white">
        Permission Overrides
      </h3>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>

    {/* Add new override */}
    <div className="flex gap-2 items-end">
      <div className="flex-1">
        <label className="text-sm font-medium mb-1 block text-gray-700 dark:text-gray-200">
          Permission
        </label>
        <Select
          disabled={permissionsLoading || overrideLoading}
          onValueChange={onSelectedPermissionIdChange}
          value={selectedPermissionId}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                permissionsLoading ? "Loading..." : "Select a permission..."
              }
            />
          </SelectTrigger>
          <SelectContent>
            {availablePermissions.map((permission) => (
              <SelectItem key={permission.id} value={permission.id.toString()}>
                {permission.label}
                {permission.isCertainPathOnly && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                    CP
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-32">
        <label className="text-sm font-medium mb-1 block text-gray-700 dark:text-gray-200">
          Type
        </label>
        <Select
          disabled={overrideLoading}
          onValueChange={(value) =>
            onSelectedOverrideTypeChange(value as "grant" | "revoke")
          }
          value={selectedOverrideType}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="grant">Grant</SelectItem>
            <SelectItem value="revoke">Revoke</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        disabled={!selectedPermissionId || overrideLoading}
        onClick={onAddOverride}
        size="icon"
        type="button"
        variant="outline"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>

    {/* Current overrides list */}
    {overrides.length > 0 && (
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Current Overrides
        </label>
        <div className="border border-gray-200 dark:border-slate-700 rounded-md divide-y divide-gray-200 dark:divide-slate-700">
          {overrides.map((override) => (
            <div
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800"
              key={override.id}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    override.overrideType === "grant"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
                      : "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
                  }`}
                >
                  {override.overrideType}
                </span>
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {override.permissionLabel}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({override.permissionInternalName})
                </span>
              </div>
              <Button
                disabled={overrideLoading}
                onClick={() => onRemoveOverride(override.id)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    )}

    {overrides.length === 0 && (
      <p className="text-sm text-muted-foreground italic">
        No permission overrides configured.
      </p>
    )}
  </div>
);

export default PermissionOverridesEditor;
